<template>
  <div class="site-media">
    <img
      v-if="shouldShowImage"
      :key="source || undefined"
      ref="imageElement"
      :src="source || undefined"
      :alt="alt"
      :loading="loading"
      :fetchpriority="loading === 'eager' ? 'high' : 'low'"
      decoding="async"
      :style="{ objectFit: fit }"
      @load="handleImageLoad"
      @error="handleImageError"
    />
    <div v-else class="site-media-fallback" role="img" :aria-label="`${alt}，图片暂不可用`">
      <i class="i-lucide-image-off" aria-hidden="true"></i>
      <span>{{ fallbackText }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { abortHtmlImage } from '~/utils/html-image'

  interface Props {
    alt: string
    fallbackText?: string
    fit?: 'contain' | 'cover'
    loading?: 'eager' | 'lazy'
    source?: null | string
  }

  defineOptions({
    name: 'SiteMedia',
  })

  const props = withDefaults(defineProps<Props>(), {
    fallbackText: '图片暂时无法加载',
    fit: 'cover',
    loading: 'lazy',
    source: null,
  })

  const imageElement = useTemplateRef<HTMLImageElement>('imageElement')
  const imageFailed = shallowRef(!props.source)
  const shouldShowImage = computed(() => Boolean(props.source) && !imageFailed.value)
  let imageTimeout: ReturnType<typeof setTimeout> | undefined
  let loadAborted = false

  /** 清理远程图片超时检查。 */
  function clearImageTimeout() {
    if (imageTimeout) clearTimeout(imageTimeout)
    imageTimeout = undefined
  }

  /** 取消尚未完成的封面下载，把带宽让给列表接口。 */
  function abortImageLoad() {
    const image = imageElement.value
    if (!image || image.complete) return
    loadAborted = true
    abortHtmlImage(image)
  }

  /** 图片加载成功后显示接口媒体。 */
  function handleImageLoad(event?: Event) {
    if (loadAborted || (event?.target && event.target !== imageElement.value)) return
    clearImageTimeout()
    imageFailed.value = false
  }

  /** 图片不可用时切换为明确的媒体失败态。 */
  function handleImageError(event?: Event) {
    if (loadAborted || (event?.target && event.target !== imageElement.value)) return
    if (!imageElement.value?.getAttribute('src')) return
    clearImageTimeout()
    imageFailed.value = true
  }

  /** 兼容 SSR hydration 前已经触发错误的图片。 */
  function checkHydratedImage() {
    const image = imageElement.value
    if (image?.complete && image.naturalWidth === 0) handleImageError()
  }

  /** 防止不可达远程资源长期保留空白媒体区域。 */
  function scheduleImageTimeout() {
    clearImageTimeout()
    if (!props.source || !/^https?:\/\//.test(props.source)) return

    imageTimeout = setTimeout(() => {
      if (loadAborted) return
      const image = imageElement.value
      if (!image?.complete || image.naturalWidth === 0) imageFailed.value = true
    }, 8000)
  }

  /** 媒体地址变化后重置加载状态。 */
  function resetImageState() {
    loadAborted = false
    clearImageTimeout()
    imageFailed.value = !props.source
    if (!props.source) return

    void nextTick(() => {
      checkHydratedImage()
      if (!imageFailed.value) scheduleImageTimeout()
    })
  }

  onMounted(resetImageState)
  onBeforeUnmount(() => {
    abortImageLoad()
    clearImageTimeout()
  })
  watch(
    () => props.source,
    (_source, previous) => {
      if (previous) abortImageLoad()
      resetImageState()
    }
  )
</script>

<style scoped lang="scss">
  .site-media {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: #eef0f2;
  }

  .site-media img {
    display: block;
    width: 100%;
    height: 100%;
  }

  .site-media-fallback {
    position: absolute;
    inset: 0;
    display: grid;
    gap: 10px;
    place-content: center;
    padding: 24px;
    font-size: 13px;
    color: var(--muted);
    text-align: center;
  }

  .site-media-fallback i {
    width: 30px;
    height: 30px;
    margin-inline: auto;
    color: rgb(105 82 85 / 58%);
  }
</style>
